import React from "react";
import LegalPage from "@/pages/LegalPage";

export default function Terms() {
  return (
    <LegalPage
      title="Terms & Conditions"
      lastUpdated="December 2025"
      testId="terms-page"
    >
      <p>
        These Terms & Conditions ("<strong>Terms</strong>") govern your access
        to and use of the website{" "}
        <a href="https://heritexinnovation.com">heritexinnovation.com</a> (the
        "<strong>Site</strong>") and any services, proposals, deliverables or
        engagements provided by{" "}
        <strong>Heritex Innovations Pvt. Ltd.</strong> ("<strong>Heritex</strong>",
        "<strong>we</strong>", "<strong>us</strong>", or "<strong>our</strong>").
        By accessing the Site or engaging us for any work, you agree to be bound
        by these Terms.
      </p>

      <h2>1. About Us</h2>
      <p>
        Heritex Innovations Pvt. Ltd. is a private limited company incorporated
        in India, registered in <strong>Lucknow, Uttar Pradesh</strong>, with
        our head office in <strong>Mumbai, Maharashtra, India</strong>. We are
        a custom solutions studio offering design, development, automation and
        consulting services.
      </p>

      <h2>2. Services</h2>
      <p>
        We provide custom software, design and consulting services including
        (but not limited to) mobile applications, websites, CRMs, SaaS
        platforms, e-commerce solutions, AI agents, chatbots, workflow
        automation, dashboards, integrations and technology strategy. The exact
        scope, deliverables, timelines and fees for any engagement will be
        described in a separate written proposal, statement of work or
        agreement signed between you and Heritex (the "<strong>Engagement
        Agreement</strong>").
      </p>
      <p>
        In the event of any conflict between these Terms and the Engagement
        Agreement, the Engagement Agreement shall prevail with respect to that
        specific engagement.
      </p>

      <h2>3. Eligibility & Acceptable Use</h2>
      <p>
        You must be at least 18 years old and legally capable of entering into
        a contract to engage our services. You agree not to:
      </p>
      <ul>
        <li>Use the Site or our services for any unlawful, fraudulent or harmful purpose;</li>
        <li>Attempt to gain unauthorised access to the Site, our systems or any of our clients' systems;</li>
        <li>Reverse engineer, copy or redistribute our proprietary materials except as expressly permitted;</li>
        <li>Submit false, misleading or third-party information in our contact forms.</li>
      </ul>

      <h2>4. Quotations, Proposals & Engagement</h2>
      <p>
        Any quotations or proposals issued by Heritex are valid for the period
        stated in the proposal (or 30 days, if not stated). A binding
        engagement is created only when both parties sign an Engagement
        Agreement or you accept the proposal in writing (including by email)
        and pay any required advance.
      </p>

      <h2>5. Fees, Payments & Taxes</h2>
      <ul>
        <li>Fees, milestones and payment schedules are set out in the Engagement Agreement.</li>
        <li>All fees are exclusive of applicable taxes (including GST), which will be charged additionally as per Indian law.</li>
        <li>Invoices are payable within the timeframe stated on the invoice (default: 7 days).</li>
        <li>We reserve the right to pause or suspend work on overdue accounts.</li>
      </ul>

      <h2>6. Intellectual Property</h2>
      <p>
        Upon receipt of all fees due for a given deliverable, the final
        delivered code, design assets and content created specifically for you
        under the Engagement Agreement ("<strong>Custom Deliverables</strong>")
        will be assigned to you, except for:
      </p>
      <ul>
        <li>
          Pre-existing tools, libraries, components, frameworks, templates and
          know-how owned or licensed by Heritex prior to or independently of
          the engagement ("<strong>Heritex IP</strong>"), which remain owned by
          Heritex and are licensed to you on a non-exclusive, perpetual,
          royalty-free basis solely as embedded in the Custom Deliverables;
        </li>
        <li>
          Third-party open source, libraries, APIs and SaaS components, which
          remain subject to their respective licences and terms.
        </li>
      </ul>
      <p>
        Heritex retains the right to display non-confidential aspects of the
        Custom Deliverables (e.g. screenshots, project name, category) as part
        of its portfolio, unless an NDA states otherwise.
      </p>

      <h2>7. Client Responsibilities</h2>
      <ul>
        <li>Provide timely access to information, content, brand assets, credentials and stakeholders required for the engagement;</li>
        <li>Provide feedback and approvals within agreed timelines;</li>
        <li>Ensure that any content, data, materials or trademarks provided to us are owned by you or licensed for our use;</li>
        <li>Comply with all applicable laws relating to your business, including data protection.</li>
      </ul>

      <h2>8. Confidentiality</h2>
      <p>
        Each party shall keep confidential any non-public information disclosed
        by the other party during the engagement and use it solely for the
        purpose of performing or receiving the services.
      </p>

      <h2>9. Warranties & Disclaimers</h2>
      <p>
        We will perform our services with reasonable skill and care, consistent
        with industry standards. Except as expressly stated in the Engagement
        Agreement, the Site and all deliverables are provided on an
        "<strong>as is</strong>" and "<strong>as available</strong>" basis. We
        make no warranties of merchantability, fitness for a particular
        purpose, uninterrupted availability or that the deliverables will be
        free of all bugs or vulnerabilities.
      </p>

      <h2>10. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, Heritex's total aggregate
        liability arising out of or in connection with the Site or any
        engagement shall not exceed the total fees paid by you to Heritex
        under the relevant Engagement Agreement in the six (6) months
        immediately preceding the event giving rise to the liability.
      </p>
      <p>
        In no event shall Heritex be liable for indirect, incidental, special,
        consequential or punitive damages, including loss of profit, revenue,
        data, goodwill or business opportunity.
      </p>

      <h2>11. Indemnity</h2>
      <p>
        You agree to indemnify and hold Heritex harmless from any third-party
        claims arising from (a) your use of the deliverables in violation of
        these Terms or applicable law, (b) any content or materials you
        provided to us, or (c) your breach of these Terms.
      </p>

      <h2>12. Termination</h2>
      <p>
        Either party may terminate an engagement as set out in the Engagement
        Agreement. On termination, you shall pay for all work performed up to
        the date of termination and any non-cancellable third-party costs
        committed on your behalf.
      </p>

      <h2>13. Governing Law & Jurisdiction</h2>
      <p>
        These Terms are governed by the laws of India. Subject to any
        arbitration clause in the Engagement Agreement, the courts at{" "}
        <strong>Mumbai, Maharashtra</strong> shall have exclusive jurisdiction
        over any disputes arising out of or in connection with these Terms.
      </p>

      <h2>14. Changes to these Terms</h2>
      <p>
        We may update these Terms from time to time. The "Last updated" date
        at the top reflects the most recent revision. Continued use of the
        Site or our services after a change constitutes acceptance of the
        updated Terms.
      </p>

      <h2>15. Contact</h2>
      <p>
        For any questions about these Terms, please contact us at:
      </p>
      <p>
        <strong>Heritex Innovations Pvt. Ltd.</strong>
        <br />
        Registered Office: Lucknow, Uttar Pradesh, India
        <br />
        Head Office: Mumbai, Maharashtra, India
        <br />
        Phone / WhatsApp:{" "}
        <a href="tel:+919873117605">+91 98731 17605</a>
        <br />
        Email:{" "}
        <a href="mailto:hello@heritexinnovation.com">hello@heritexinnovation.com</a>
      </p>
    </LegalPage>
  );
}
